import * as THREE from "three";
import { GLTFLoader } from "three/examples/jsm/loaders/GLTFLoader.js";

const H = 1.72;
const ASPECT = 595 / 1225;
const TIP = new THREE.Vector3((0.264 - 0.5) * H * ASPECT, (1 - 0.218) * H, 0.05);

function smokeTexture() {
  const c = document.createElement("canvas");
  c.width = c.height = 64;
  const g = c.getContext("2d");
  const grad = g.createRadialGradient(32, 32, 0, 32, 32, 32);
  grad.addColorStop(0, "rgba(255,255,255,0.9)");
  grad.addColorStop(0.4, "rgba(255,255,255,0.35)");
  grad.addColorStop(1, "rgba(255,255,255,0)");
  g.fillStyle = grad;
  g.fillRect(0, 0, 64, 64);
  return new THREE.CanvasTexture(c);
}

export function buildGirl(scene, disposables, { position, rotationY, viewportHeight, modelUrl = "/models/girl.glb" }) {
  const root = new THREE.Group();
  root.position.copy(position);
  root.rotation.y = rotationY;
  scene.add(root);

  // photoreal cutout, lit by the scene lights so she sits in the space
  const tex = new THREE.TextureLoader().load("/models/girl.png");
  tex.colorSpace = THREE.SRGBColorSpace;
  tex.anisotropy = 8;
  const mat = new THREE.MeshStandardMaterial({
    map: tex,
    transparent: true,
    alphaTest: 0.35,
    roughness: 0.85,
    side: THREE.DoubleSide,
  });
  const geo = new THREE.PlaneGeometry(H * ASPECT, H);
  disposables.push(tex, mat, geo);
  const figure = new THREE.Mesh(geo, mat);
  figure.position.y = H / 2;
  root.add(figure);

  // contact shadow
  const shadowTex = smokeTexture();
  const shadowMat = new THREE.MeshBasicMaterial({
    map: shadowTex,
    color: 0x000000,
    transparent: true,
    opacity: 0.7,
    depthWrite: false,
  });
  const shadowGeo = new THREE.PlaneGeometry(1.1, 0.5);
  disposables.push(shadowTex, shadowMat, shadowGeo);
  const shadow = new THREE.Mesh(shadowGeo, shadowMat);
  shadow.rotation.x = -Math.PI / 2;
  shadow.position.set(0, 0.012, 0.05);
  root.add(shadow);

  new GLTFLoader().load(
    modelUrl,
    (gltf) => {
      const model = gltf.scene;
      const box = new THREE.Box3().setFromObject(model);
      const size = box.getSize(new THREE.Vector3());
      model.scale.setScalar(H / size.y);
      box.setFromObject(model);
      model.position.y -= box.min.y;
      root.remove(figure);
      root.add(model);
    },
    undefined,
    () => {}
  );

  const emberMat = new THREE.MeshStandardMaterial({ color: 0x300000, emissive: 0xff5a1f, emissiveIntensity: 3 });
  const emberGeo = new THREE.SphereGeometry(0.011, 6, 6);
  disposables.push(emberMat, emberGeo);
  const ember = new THREE.Mesh(emberGeo, emberMat);
  ember.position.copy(TIP);
  root.add(ember);
  const emberLight = new THREE.PointLight(0xff6a2a, 1.2, 1.6, 2);
  emberLight.position.copy(TIP);
  root.add(emberLight);

  const N = 90;
  const pos = new Float32Array(N * 3);
  const aT = new Float32Array(N);
  const seeds = new Float32Array(N);
  for (let i = 0; i < N; i++) {
    aT[i] = Math.random();
    seeds[i] = Math.random() * 6.28;
  }
  const smokeGeo = new THREE.BufferGeometry();
  smokeGeo.setAttribute("position", new THREE.BufferAttribute(pos, 3));
  smokeGeo.setAttribute("aT", new THREE.BufferAttribute(aT, 1));
  const smokeTex = smokeTexture();
  const smokeMat = new THREE.ShaderMaterial({
    uniforms: { map: { value: smokeTex }, uScale: { value: viewportHeight } },
    transparent: true,
    depthWrite: false,
    vertexShader: [
      "attribute float aT;",
      "uniform float uScale;",
      "varying float vA;",
      "void main(){",
      "  vA = (1.0 - aT) * smoothstep(0.0, 0.12, aT);",
      "  vec4 mv = modelViewMatrix * vec4(position, 1.0);",
      "  gl_PointSize = (0.05 + aT * 0.34) * uScale / max(-mv.z, 0.2);",
      "  gl_Position = projectionMatrix * mv;",
      "}",
    ].join("\n"),
    fragmentShader: [
      "uniform sampler2D map;",
      "varying float vA;",
      "void main(){",
      "  vec4 t = texture2D(map, gl_PointCoord);",
      "  gl_FragColor = vec4(vec3(0.78, 0.88, 0.92), t.a * vA * 0.3);",
      "}",
    ].join("\n"),
  });
  disposables.push(smokeGeo, smokeMat, smokeTex);
  const smoke = new THREE.Points(smokeGeo, smokeMat);
  smoke.frustumCulled = false;
  root.add(smoke);

  const update = (t, dt) => {
    const drag = Math.max(0, Math.sin(t * 0.55)) ** 6;
    emberMat.emissiveIntensity = 2 + drag * 4 + (Math.random() - 0.5) * 0.4;
    emberLight.intensity = 0.7 + drag * 2.4;
    figure.position.y = H / 2 + Math.sin(t * 0.9) * 0.004;

    const p = smokeGeo.attributes.position.array;
    const a = smokeGeo.attributes.aT.array;
    for (let i = 0; i < N; i++) {
      a[i] += dt * 0.17;
      if (a[i] > 1) a[i] -= 1;
      const k = a[i];
      const s = seeds[i];
      p[i * 3] = TIP.x + Math.sin(k * 3.2 + s) * 0.09 * k - k * 0.1;
      p[i * 3 + 1] = TIP.y + k * 0.95;
      p[i * 3 + 2] = TIP.z + Math.cos(k * 2.6 + s) * 0.07 * k + k * 0.16;
    }
    smokeGeo.attributes.position.needsUpdate = true;
    smokeGeo.attributes.aT.needsUpdate = true;
  };

  return { root, update, setViewport: (h) => (smokeMat.uniforms.uScale.value = h) };
}
